import { useMutation } from '@tanstack/react-query';
import RejectionField from '@/src/app/components/dialog/RejectionField';
import LoadingButton from '@/src/app/components/inputs/LoadingButton';
import ConfirmationDialog from '@/src/app/components/messages/ConfirmationDialog';
import ErrorToast, {
  showToast,
} from '@/src/app/components/messages/ErrorAlert';
import { useCompanyContext } from '@/src/app/context/CompanyContext';
import { useProcessContext } from '@/src/app/context/ProcessContext';
import {
  BatchType,
  DraftBatchPayment,
  PaymentBatch,
  PaymentBatchPayable,
} from '@/src/app/Interfaces/Accounting/AccountsPayables.interfaces';
import { ApiError } from '@/src/app/Interfaces/system/Generic.interfaces';
import { Access } from '@/src/app/Interfaces/admin/Role.interfaces';
import {
  approvePaymentBatch,
  postPaymentBatch,
  putPaymentBatch,
  rejectPaymentBatch,
  submitPaymentBatch,
  uploadPaymentBatch,
} from '@/src/app/services/accounting/AccountsPayablesService';
import { AxiosError } from 'axios';
import { Toast } from 'primereact/toast';
import React, { useEffect, useRef, useState } from 'react';

type PaymentBatchProcessProps = {
  batch: DraftBatchPayment[];
  defaultDraftID?: PaymentBatch['id'];
  afterUpload: () => void;
  afterSaving?: (draftID: string) => void;
  type: BatchType;
  resetDraft: () => void;
  approvalMode: boolean;
  access: Access;
  afterApproval?: (draftID: string) => void;
};

const PaymentBatchProcess = ({
  batch,
  defaultDraftID,
  afterUpload,
  afterSaving,
  type,
  resetDraft,
  approvalMode,
  access,
  afterApproval,
}: PaymentBatchProcessProps) => {
  const { selectedCompany } = useCompanyContext();
  const processStatus = useProcessContext();
  const [draftID, setDraftID] = useState<string>(defaultDraftID ?? '');
  const [visibility, setVisibility] = useState(false);
  const toast = useRef<Toast>(null);
  const prevDraft = useRef(batch);

  const save = useMutation({
    mutationFn: (draftID: string) => {
      const cleanedBatch = batch.reduce((acc, value) => {
        const { apref, vendor, aptrans, payType, job, type } = value;
        if (apref && vendor && aptrans && (payType || job)) {
          acc.push({ apref, aptrans, vendor, payType, job, type });
        }

        return acc;
      }, [] as PaymentBatchPayable[]);

      if (draftID) {
        return putPaymentBatch(draftID, cleanedBatch, approvalMode);
      }

      return postPaymentBatch(selectedCompany!, cleanedBatch, type);
    },
    onSuccess: (data) => {
      showToast(
        'success',
        toast,
        'Save Payment Batch',
        'The data was saved successfully!',
        3000
      );

      processStatus.setSave(false);
      if (approvalMode || access.editable === 'no-approval') {
        processStatus.setApprove(true);
      } else {
        processStatus.setSubmit(true);
      }

      if (!draftID) {
        setDraftID(data);
      }

      if (afterSaving) {
        afterSaving(draftID || data);
      }
    },
    onError: (error: ApiError) => {
      const errorMessage = error.response?.data?.message;

      showToast(
        'error',
        toast,
        'Save Payment Batch',
        errorMessage ?? "The data couldn't be saved",
        3000
      );
    },
  });

  const submit = useMutation({
    mutationFn: (draftID: string) => {
      return submitPaymentBatch(draftID);
    },
    onSuccess: () => {
      processStatus.setSubmit(false);

      showToast(
        'success',
        toast,
        'Submit Payment Batch',
        'The data was submitted successfully!',
        3000
      );
    },
    onError: () => {
      showToast(
        'error',
        toast,
        'Submit Payment Batch',
        "The data couldn't be submitted",
        3000
      );
    },
  });

  const approveRequest = useMutation({
    mutationFn: (draftID: string) => {
      return approvePaymentBatch(draftID);
    },
    onSuccess: () => {
      showToast(
        'success',
        toast,
        `Approve Payment Batch`,
        `The draft was approved successfully!`,
        3000
      );

      processStatus.setApprove(false);
      processStatus.setAlreadyApproved(true);
      if (afterApproval) afterApproval(draftID);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };
      showToast(
        'error',
        toast,
        `Approve Payment Batch`,
        errorData?.message || `The draft couldn't be approved`,
        3000
      );
    },
  });

  const uploadRequest = useMutation({
    mutationFn: (draftID: string) => {
      return uploadPaymentBatch(draftID);
    },
    onSuccess: () => {
      showToast(
        'success',
        toast,
        `Upload Payment Batch`,
        `The draft was uploaded successfully!`,
        3000
      );

      processStatus.setApprove(false);

      if (afterUpload) {
        afterUpload();
      } else {
        processStatus.setAlreadyApproved(true);
      }
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };
      showToast(
        'error',
        toast,
        `Upload Payment Batch`,
        errorData?.message || `The draft couldn't be uploaded`,
        3000
      );
    },
  });

  const rejectRequest = useMutation({
    mutationFn: (props: { draftID: string; reason: string }) => {
      return rejectPaymentBatch(props.draftID, props.reason);
    },
    onSuccess: () => {
      showToast(
        'success',
        toast,
        `Reject Payment Batch`,
        `The draft was rejected successfully!`,
        3000
      );

      processStatus.setApprove(false);
      processStatus.setAlreadyApproved(true);
      if (afterApproval) afterApproval(draftID);
    },
    onError: (error: AxiosError) => {
      const errorData = error.response?.data as {
        code: string;
        message: string;
      };

      showToast(
        'error',
        toast,
        `Reject Payment Batch`,
        errorData?.message || `The draft couldn't be rejected`,
        3000
      );
    },
  });

  useEffect(() => {
    if (prevDraft.current !== batch && !processStatus.alreadyApproved) {
      processStatus.setSave(true);
      processStatus.setSubmit(false);
      processStatus.setApprove(false);
      prevDraft.current = batch;
    }
  }, [batch, processStatus]);

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />
      <div className="sm:mr-auto">
        <ConfirmationDialog
          tagKey="draft-clear"
          Button={
            <LoadingButton
              label="Clear Form"
              fontSize="text-xl"
              bgColor="bg-orange-500"
              isLoading={false}
              type="button"
            />
          }
          onConfirm={() => {
            resetDraft();
          }}
          message={'Are you sure you want to clear the form data?'}
        />
      </div>
      <div className="flex flex-wrap gap-4">
        <LoadingButton
          label="Save"
          fontSize="text-xl"
          bgColor="buttonSecondary"
          isLoading={save.isLoading}
          onClick={() => save.mutate(draftID)}
          disabled={!processStatus.save || save.isLoading}
        />
        {!approvalMode && access.editable === 'approval' && (
          <ConfirmationDialog
            Button={
              <LoadingButton
                label="Submit"
                fontSize="text-xl"
                isLoading={submit.isLoading}
                disabled={!processStatus.submit || submit.isLoading}
                type="button"
              />
            }
            onConfirm={() => submit.mutate(draftID)}
            message="Are you sure you want to submit this payment batch?"
          />
        )}
        {approvalMode && access.shouldApprove && (
          <ConfirmationDialog
            tagKey="draft-rejection"
            visibility={visibility}
            Button={
              <LoadingButton
                label={'Reject'}
                fontSize="text-xl"
                bgColor="bluwaiRed"
                isLoading={rejectRequest.isLoading}
                disabled={!processStatus.approve || rejectRequest.isLoading}
                type="button"
                onClick={() => setVisibility(true)}
              />
            }
            contentClassName="pb-0"
            onConfirm={() => {}}
            acceptClassName="hidden"
            rejectClassName="hidden"
            onHide={() => setVisibility(false)}
            message={
              <RejectionField
                onReject={() => {
                  setVisibility(false);
                }}
                onConfirm={(reason) => {
                  rejectRequest.mutate({ draftID, reason });
                  setVisibility(false);
                }}
              />
            }
          />
        )}
        {approvalMode && access.shouldApprove && (
          <ConfirmationDialog
            tagKey="draft-approval"
            Button={
              <LoadingButton
                label={'Approve'}
                fontSize="text-xl"
                bgColor="bg-green-500"
                isLoading={approveRequest.isLoading}
                disabled={!processStatus.approve || approveRequest.isLoading}
                type="button"
              />
            }
            onConfirm={() => {
              approveRequest.mutate(draftID);
            }}
            message={`Are you sure you want to approve this draft?`}
          />
        )}
        {!approvalMode && access.editable === 'no-approval' && (
          <ConfirmationDialog
            tagKey="draft-upload"
            Button={
              <LoadingButton
                label={'Upload'}
                fontSize="text-xl"
                bgColor="bg-green-500"
                isLoading={uploadRequest.isLoading}
                disabled={!processStatus.approve || uploadRequest.isLoading}
                type="button"
              />
            }
            onConfirm={() => {
              uploadRequest.mutate(draftID);
            }}
            message={`Are you sure you want to upload this draft?`}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default PaymentBatchProcess;
